/**
 * Per-store dashboard at /it/netmon/:site_id.
 *
 * Charts (Recharts):
 *   - Latency timeline (LineChart, one line per ICMP target)
 *   - Jitter timeline (AreaChart)
 *   - Packet loss (BarChart)
 *   - DNS performance (LineChart)
 *   - BOH bandwidth (AreaChart with p95 band overlay)
 *   - LAN device count trend (LineChart)
 *
 * Tables:
 *   - Events feed with Acknowledge button (admin only)
 *   - LAN devices grouped by MAC, last-seen-stale shown in grey
 *
 * Time-range selector: 1h / 6h / 24h / 7d / 30d.
 */

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowLeft,
  Check,
  Info,
  Loader2,
  Radio,
} from 'lucide-react';

import { Pill } from '@/components/Pill';
import { gridTheadClassName } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { useSession } from '@/lib/useSession';
import {
  hasNetmonAdmin,
  healthBadgeClass,
  healthDotClass,
  internetDownText,
  internetUnknownText,
  pcNotReportingText,
  formatLocalTime,
  relativeTime,
  useAckNetmonEvent,
  ispProviderLabel,
  useNetmonDevices,
  useNetmonEvents,
  useNetmonSamples,
  useNetmonSites,
  type Health,
  type NetmonEvent,
  type NetmonSample,
  type TimeRange,
} from '@/lib/useNetmon';

const TIME_RANGES: TimeRange[] = ['1h', '6h', '24h', '7d', '30d'];

function fmtX(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

type LatencyPoint = { collected_at: string; [target: string]: number | string | null };

function pivotByTarget(
  samples: NetmonSample[],
  field: keyof NetmonSample,
): { points: LatencyPoint[]; targets: string[] } {
  const targets = Array.from(new Set(samples.map((s) => s.target))).sort();
  const byTs = new Map<string, LatencyPoint>();
  for (const s of samples) {
    const ts = s.collected_at;
    let row = byTs.get(ts);
    if (!row) {
      row = { collected_at: ts };
      byTs.set(ts, row);
    }
    const v = s[field];
    row[s.target] = typeof v === 'number' ? v : v == null ? null : Number(v) || null;
  }
  const points = Array.from(byTs.values()).sort((a, b) =>
    a.collected_at < b.collected_at ? -1 : 1,
  );
  return { points, targets };
}

const LINE_COLORS = ['#8B5A3C', '#5C7A29', '#3D6BA8', '#A8893D', '#7F3D8C', '#3D8C82'];

function ChartCard({
  title,
  children,
  rightSlot,
}: {
  title: string;
  children: React.ReactNode;
  rightSlot?: React.ReactNode;
}) {
  return (
    <div className="border border-amp-line rounded-lg bg-amp-white dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <div className="border-b border-amp-line dark:border-amp-dark-line px-4 py-3 flex items-center justify-between">
        <h2 className="text-[16px] font-semibold text-amp-ink dark:text-amp-dark-ink">{title}</h2>
        {rightSlot}
      </div>
      <div className="px-2 py-3 h-64">{children}</div>
    </div>
  );
}

function HealthBadge({ h }: { h: Health }) {
  const label =
    h === 'green'
      ? 'Healthy'
      : h === 'yellow'
        ? 'Degraded'
        : h === 'orange'
          ? 'Critical'
          : 'Down';
  return (
    <Pill tone={healthBadgeClass(h)}>
      <span className={`h-2 w-2 rounded-full ${healthDotClass(h)}`} aria-hidden />
      {label}
    </Pill>
  );
}

function p95(values: number[]): number | null {
  const nums = values.filter((v) => Number.isFinite(v));
  if (nums.length === 0) return null;
  nums.sort((a, b) => a - b);
  const idx = Math.min(nums.length - 1, Math.floor(nums.length * 0.95));
  return nums[idx] ?? null;
}

export function NetmonSiteDetail() {
  const { site_id } = useParams<{ site_id: string }>();
  const { user } = useSession();
  const isAdmin = hasNetmonAdmin(user?.groups, user?.superAdmin);

  const sitesQ = useNetmonSites();
  const site = sitesQ.sites.find((s) => s.site_id === site_id);

  const [range, setRange] = useState<TimeRange>('24h');

  const samplesQ = useNetmonSamples(site_id, range);
  const eventsQ = useNetmonEvents(site_id, range);
  const devicesQ = useNetmonDevices(site_id);
  const ackMut = useAckNetmonEvent();

  const samples = useMemo(() => samplesQ.data?.data.samples ?? [], [samplesQ.data]);
  const icmp = useMemo(() => samples.filter((s) => s.test_type === 'icmp'), [samples]);
  const dns = useMemo(() => samples.filter((s) => s.test_type === 'dns'), [samples]);
  const bandwidth = useMemo(
    () => samples.filter((s) => s.test_type === 'bandwidth'),
    [samples],
  );
  const lanCount = useMemo(
    () => samples.filter((s) => s.test_type === 'lan_count'),
    [samples],
  );

  const latency = useMemo(() => pivotByTarget(icmp, 'avg_latency_ms'), [icmp]);
  const jitter = useMemo(() => pivotByTarget(icmp, 'jitter_ms'), [icmp]);
  const loss = useMemo(() => pivotByTarget(icmp, 'loss_pct'), [icmp]);
  const dnsLatency = useMemo(() => pivotByTarget(dns, 'dns_resolve_ms'), [dns]);

  const bandwidthSeries = useMemo(
    () =>
      bandwidth.map((s) => ({
        collected_at: s.collected_at,
        sent: s.bytes_sent_per_sec ?? null,
        recv: s.bytes_recv_per_sec ?? null,
        mbps: s.throughput_mbps ?? null,
      })),
    [bandwidth],
  );
  const bandwidthP95 = useMemo(
    () =>
      p95(bandwidthSeries.map((b) => Number(b.mbps ?? 0)).filter((v) => v > 0)),
    [bandwidthSeries],
  );

  const lanCountSeries = useMemo(
    () =>
      lanCount.map((s) => ({
        collected_at: s.collected_at,
        count: s.lan_device_count ?? null,
      })),
    [lanCount],
  );

  const events = eventsQ.data?.data.events ?? [];
  const devices = devicesQ.data?.data.devices ?? [];

  if (sitesQ.isLoading) {
    return (
      <div className="inline-flex items-center gap-2 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading site...
      </div>
    );
  }
  if (!site) {
    return (
      <div>
        <Link to="/it/netmon" className="text-sm text-amp-ink-muted hover:text-amp-ink inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Back to sites
        </Link>
        <div className="mt-4 border border-amp-line rounded-lg bg-amp-white p-6 dark:border-amp-dark-line dark:bg-amp-dark-surface">
          <h2 className="text-[16px] font-semibold text-amp-ink dark:text-amp-dark-ink">Site not found.</h2>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Site {site_id} does not exist or is not active. Sites register on first ingest.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Link to="/it/netmon" className="text-sm text-amp-ink-muted hover:text-amp-ink inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="h-3 w-3" /> Back to sites
      </Link>
      <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">
            IT - Network monitor
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink flex items-center gap-2">
            #{site.site_id} {site.site_name}
            {site.cellular_primary === 1 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 px-2 py-0.5 text-xs font-medium dark:bg-blue-900/40 dark:text-blue-300">
                <Radio className="h-3 w-3" /> Cellular
              </span>
            )}
            <HealthBadge h={site.health} />
          </h1>
          <p
            className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted"
            title={site.last_sample_at ? formatLocalTime(site.last_sample_at) : undefined}
          >
            {site.transport === 'cellular'
              ? 'Cellular'
              : site.transport === 'wired'
                ? 'Wired'
                : 'Unknown transport'}
            {' - provider '}
            {ispProviderLabel(site.isp_provider)}
            {' - last sample '}
            {relativeTime(site.last_sample_at)}
          </p>
          {[internetDownText(site), internetUnknownText(site), pcNotReportingText(site)]
            .filter((n): n is string => !!n)
            .map((n) => (
              <p key={n} className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mt-0.5">
                {n}
              </p>
            ))}
        </div>
        <div className="inline-flex border border-amp-line rounded-md overflow-hidden dark:border-amp-dark-line">
          {TIME_RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 text-xs ${
                range === r
                  ? 'bg-amp-olive text-white dark:bg-amp-olive-dark'
                  : 'bg-amp-white text-amp-ink hover:bg-amp-cream-warm dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Latency (ms) by ICMP target">
          {samplesQ.isLoading ? (
            <Loading />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={latency.points} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e2d3" />
                <XAxis dataKey="collected_at" tickFormatter={fmtX} fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {latency.targets.map((t, i) => (
                  <Line
                    key={t}
                    type="monotone"
                    dataKey={t}
                    stroke={LINE_COLORS[i % LINE_COLORS.length]}
                    dot={false}
                    strokeWidth={1.5}
                    connectNulls={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Jitter (ms)">
          {samplesQ.isLoading ? (
            <Loading />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={jitter.points}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e2d3" />
                <XAxis dataKey="collected_at" tickFormatter={fmtX} fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {jitter.targets.map((t, i) => (
                  <Area
                    key={t}
                    type="monotone"
                    dataKey={t}
                    stroke={LINE_COLORS[i % LINE_COLORS.length]}
                    fill={LINE_COLORS[i % LINE_COLORS.length]}
                    fillOpacity={0.2}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Packet loss (%)">
          {samplesQ.isLoading ? (
            <Loading />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={loss.points}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e2d3" />
                <XAxis dataKey="collected_at" tickFormatter={fmtX} fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {loss.targets.map((t, i) => (
                  <Bar
                    key={t}
                    dataKey={t}
                    stackId="a"
                    fill={LINE_COLORS[i % LINE_COLORS.length]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="DNS resolve time (ms)">
          {samplesQ.isLoading ? (
            <Loading />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dnsLatency.points}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e2d3" />
                <XAxis dataKey="collected_at" tickFormatter={fmtX} fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {dnsLatency.targets.map((t, i) => (
                  <Line
                    key={t}
                    type="monotone"
                    dataKey={t}
                    stroke={LINE_COLORS[i % LINE_COLORS.length]}
                    dot={{ r: 2 }}
                    strokeWidth={1.5}
                    connectNulls={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard
          title={
            'BOH bandwidth (mbps)' +
            (bandwidthP95 != null ? ` — p95 ${bandwidthP95.toFixed(1)}` : '')
          }
        >
          {samplesQ.isLoading ? (
            <Loading />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={bandwidthSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e2d3" />
                <XAxis dataKey="collected_at" tickFormatter={fmtX} fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {bandwidthP95 != null && (
                  <ReferenceLine
                    y={bandwidthP95}
                    stroke="#A8893D"
                    strokeDasharray="4 4"
                    label={{ value: 'p95', fontSize: 10 }}
                  />
                )}
                <Area
                  type="monotone"
                  dataKey="mbps"
                  stroke="#5C7A29"
                  fill="#5C7A29"
                  fillOpacity={0.25}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="LAN device count">
          {samplesQ.isLoading ? (
            <Loading />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={lanCountSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e2d3" />
                <XAxis dataKey="collected_at" tickFormatter={fmtX} fontSize={10} />
                <YAxis fontSize={10} allowDecimals={false} />
                <Tooltip />
                <Line
                  type="stepAfter"
                  dataKey="count"
                  stroke="#3D6BA8"
                  dot={{ r: 2 }}
                  strokeWidth={1.5}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <div className="mt-4 rounded-md border border-amp-line dark:border-amp-dark-line bg-amp-cream-warm dark:bg-amp-dark-surface-warm px-3 py-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted inline-flex items-start gap-2">
        <Info className="h-3 w-3 mt-0.5" />
        Bandwidth and device counts show what the BOH PC can see on its own LAN segment.
        Guest Wi-Fi and Private Wi-Fi devices are on separate VLANs that the BOH PC cannot observe.
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <EventsCard events={events} isLoading={eventsQ.isLoading} isAdmin={isAdmin} onAck={(id) => ackMut.mutate(id)} ackPending={ackMut.isPending} />
        <DevicesCard devices={devices} isLoading={devicesQ.isLoading} />
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div className="h-full w-full flex items-center justify-center text-amp-ink-muted dark:text-amp-dark-ink-muted">
      <Loader2 className="h-4 w-4 animate-spin" />
    </div>
  );
}

function EventsCard({
  events,
  isLoading,
  isAdmin,
  onAck,
  ackPending,
}: {
  events: NetmonEvent[];
  isLoading: boolean;
  isAdmin: boolean;
  onAck: (id: number) => void;
  ackPending: boolean;
}) {
  return (
    <DataGridShell
      title="Events"
      loaded={events.length}
      countNoun="events"
      className="h-[420px]"
    >
      <table className="w-full text-xs">
        <thead className={gridTheadClassName}>
          <tr>
            <th className="px-4 py-2 text-left">When</th>
            <th className="px-4 py-2 text-left">Type</th>
            <th className="px-4 py-2 text-left">Dir</th>
            <th className="px-4 py-2 text-left">Detail</th>
            <th className="px-4 py-2 text-right">Ack</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
          {isLoading ? (
            <tr><td colSpan={5} className="px-4 py-6 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading events...</td></tr>
          ) : events.length === 0 ? (
            <tr><td colSpan={5} className="px-4 py-6 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No events in this window.</td></tr>
          ) : (
            events.map((e) => (
              <tr key={e.id}>
                <td className="px-4 py-2 font-mono text-[12px] text-amp-ink dark:text-amp-dark-ink" title={formatLocalTime(e.detected_at)}>{formatLocalTime(e.detected_at)}</td>
                <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">{e.event_type}</td>
                <td className="px-4 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">{e.direction ?? ''}</td>
                <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">{e.detail ?? ''}</td>
                <td className="px-4 py-2 text-right">
                  {e.acknowledged ? (
                    <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-300">
                      <Check className="h-3 w-3" /> {e.acknowledged_by ?? 'ack'}
                    </span>
                  ) : isAdmin ? (
                    <button
                      type="button"
                      disabled={ackPending}
                      onClick={() => onAck(e.id)}
                      className="text-xs border border-amp-line text-amp-ink rounded px-2 py-1 hover:bg-amp-cream-warm disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
                    >
                      Acknowledge
                    </button>
                  ) : (
                    <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">--</span>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </DataGridShell>
  );
}

function DevicesCard({
  devices,
  isLoading,
}: {
  devices: Array<{
    mac: string;
    ip: string | null;
    hostname: string | null;
    oui_vendor: string | null;
    first_seen: string | null;
    last_seen: string | null;
    reconcile_status: string | null;
  }>;
  isLoading: boolean;
}) {
  return (
    <DataGridShell
      title="LAN devices"
      loaded={devices.length}
      countNoun="LAN devices"
      className="h-[420px]"
    >
      <table className="w-full text-xs">
        <thead className={gridTheadClassName}>
          <tr>
            <th className="px-4 py-2 text-left">MAC</th>
            <th className="px-4 py-2 text-left">IP</th>
            <th className="px-4 py-2 text-left">Hostname</th>
            <th className="px-4 py-2 text-left">Vendor</th>
            <th className="px-4 py-2 text-left">First seen</th>
            <th className="px-4 py-2 text-left">Last seen</th>
            <th className="px-4 py-2 text-left">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
          {isLoading ? (
            <tr><td colSpan={7} className="px-4 py-6 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading devices...</td></tr>
          ) : devices.length === 0 ? (
            <tr><td colSpan={7} className="px-4 py-6 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No devices discovered yet.</td></tr>
          ) : (
            devices.map((d) => {
              const lastSeenTs = d.last_seen ? new Date(d.last_seen).getTime() : 0;
              const stale = lastSeenTs > 0 && Date.now() - lastSeenTs > 7 * 24 * 60 * 60 * 1000;
              const grey = stale ? 'text-amp-ink-muted dark:text-amp-dark-ink-muted' : 'text-amp-ink dark:text-amp-dark-ink';
              return (
                <tr key={d.mac} className={grey}>
                  <td className="px-4 py-2 font-mono text-[12px]">{d.mac}</td>
                  <td className="px-4 py-2">{d.ip ?? ''}</td>
                  <td className="px-4 py-2">{d.hostname ?? ''}</td>
                  <td className="px-4 py-2">{d.oui_vendor ?? ''}</td>
                  <td className="px-4 py-2" title={d.first_seen ?? undefined}>{formatLocalTime(d.first_seen)}</td>
                  <td className="px-4 py-2" title={d.last_seen ?? undefined}>{formatLocalTime(d.last_seen)}</td>
                  <td className="px-4 py-2">
                    {d.reconcile_status === 'unknown' ? (
                      <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300">
                        ⚠️ unknown
                      </span>
                    ) : (
                      d.reconcile_status ?? '--'
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </DataGridShell>
  );
}
